import { Box } from "@mui/material";
import { Divider } from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import FactoryOutlinedIcon from "@mui/icons-material/FactoryOutlined";
import ChecklistIcon from "@mui/icons-material/Checklist";
import DirectionsWalkIcon from "@mui/icons-material/DirectionsWalk";
import FormatListBulletedOutlinedIcon from "@mui/icons-material/FormatListBulletedOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import MedicalServicesOutlinedIcon from "@mui/icons-material/MedicalServicesOutlined";
import ShowChartOutlinedIcon from "@mui/icons-material/ShowChartOutlined";
import PrintIcon from "@mui/icons-material/Print";
import ViewWeekIcon from "@mui/icons-material/ViewWeek";
import EditIcon from "@mui/icons-material/Edit";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import AutoFixHighOutlinedIcon from "@mui/icons-material/AutoFixHighOutlined";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import WidgetsOutlinedIcon from "@mui/icons-material/WidgetsOutlined";

import { AtomicLogo } from "../../assets/AtomicLogo";
import { MenuItem } from "./menu_item_component";
import { useCustomer } from "../../contexts/app/provider";
import { useContext, useMemo } from "react";
import {
  convertToClientFeatures,
  type ClientFeatureFlags,
} from "../../types/feature_flags";
import { LayoutContext } from "../../contexts/layout/context";
import { ChatContext } from "../../contexts/chat/context";
import { VersionedDataContext } from "../../contexts/versioned_data/context";
import { useGuardedNavigation } from "../../hooks/use_guarded_navigation";

export const LeftMenu = () => {
  const { customerSettings } = useCustomer();
  const location = useLocation();
  const { isSidebarOpen, setIsSidebarOpen } = useContext(LayoutContext);
  const { connectChat, disconnectChat } = useContext(ChatContext);
  const { mainRun, lastSuccessfulRun } = useContext(VersionedDataContext);
  const { navigateWithGuards } = useGuardedNavigation();
  const features: ClientFeatureFlags = useMemo(
    () => convertToClientFeatures(customerSettings?.features),
    [customerSettings],
  );
  const activeRun = mainRun || lastSuccessfulRun;

  const handleNucleusClick = () => {
    if (location.pathname === "/chat") {
      navigateWithGuards("/chat");
      return;
    }

    if (isSidebarOpen) {
      setIsSidebarOpen(false);
      disconnectChat();
      return;
    }

    if (activeRun) {
      setIsSidebarOpen(true);
      connectChat(activeRun, "onboarding");
      return;
    }

    setIsSidebarOpen(true);
  };

  return (
    <Box
      sx={{
        pt: 2,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        height: "100%",
      }}
    >
      {/* Logo - Always at top */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 1,
          mt: 0.5,
          justifyContent: "center",
          height: "40px",
          flexShrink: 0,
        }}
      >
        <Link to="/">
          <AtomicLogo width={30} height={30} />
        </Link>
      </Box>

      {/* Scrollable menu items container */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          width: "100%",
          overflowY: "auto",
          overflowX: "hidden",
          p: 0,
          // Custom scrollbar styling
          "&::-webkit-scrollbar": {
            width: "2px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: (theme) => theme.palette.grey[300],
            borderRadius: "2px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: (theme) => theme.palette.grey[600],
          },
        }}
      >
        <MenuItem
          title="Run History"
          link="/run-history"
          Icon={FormatListBulletedOutlinedIcon}
        />
        {features?.RunReportCard && (
          <MenuItem
            title="Run Report Card"
            link="/run-report-card"
            Icon={InsightsOutlinedIcon}
          />
        )}
        <MenuItem title="Demand" link="/demand" Icon={ShowChartOutlinedIcon} />
        {features?.ForecastTracking && (
          <MenuItem
            title="Forecast Tracking"
            link="/forecast-tracking"
            Icon={QueryStatsIcon}
          />
        )}
        {features?.ThreeForecastTracking && (
          <MenuItem
            title="Forecast Tracking"
            link="/three-forecast-tracking"
            Icon={QueryStatsIcon}
          />
        )}
        {features?.DemandWalkSingle && (
          <MenuItem
            title="Demand Walk (Beta)"
            link="/demand-walk-single"
            Icon={DirectionsWalkIcon}
          />
        )}
        {features?.DemandWalk && (
          <MenuItem
            title="Demand Walk"
            link="/demand-plan"
            Icon={DirectionsWalkIcon}
          />
        )}
        {features?.SkuCutovers && (
          <MenuItem
            title="SKU Cutovers"
            link="/sku-cutovers"
            Icon={SwapHorizIcon}
          />
        )}
        <MenuItem title="Supply" link="/supply" Icon={CategoryOutlinedIcon} />
        {features?.TransferRecommendations && (
          <MenuItem
            title="Transfer Planning"
            link="/transfer-recommendations"
            Icon={LocalShippingIcon}
          />
        )}
        {features?.InventoryHealth && (
          <MenuItem
            title="Inventory Health"
            link="/inventory-health"
            Icon={MedicalServicesOutlinedIcon}
          />
        )}
        {features?.InventoryHealthWithTransfers && (
          <MenuItem
            title="Inventory Health"
            link="/inventory-health-with-transfers"
            Icon={MedicalServicesOutlinedIcon}
          />
        )}
        {features?.InventoryHealthForHubs && (
          <MenuItem
            title="Inventory Health: Hubs"
            link="/inventory-health-for-hubs"
            Icon={MedicalServicesOutlinedIcon}
          />
        )}
        {features?.TransferRecommendationsHubToSpoke && (
          <MenuItem
            title="Inventory Health: Spokes"
            link="/transfer-recommendations-hub-to-spoke"
            Icon={LocalShippingIcon}
          />
        )}
        {features?.InventoryHealthForHubs && (
          <MenuItem
            title="Inventory"
            link="/inventory-wos"
            Icon={Inventory2OutlinedIcon}
          />
        )}
        {features?.MRP && (
          <MenuItem
            title="Inventory Health - MRP"
            link="/inventory-health-mrp"
            Icon={MedicalServicesOutlinedIcon}
          />
        )}
        {features?.RawInventoryHealth && (
          <MenuItem
            title="Inventory Health"
            link="/raw-inventory-health"
            Icon={MedicalServicesOutlinedIcon}
          />
        )}
        {features?.SupplyWalkEdit && (
          <MenuItem
            title="Supply Walk (Beta)"
            link="/supply-walk-single"
            Icon={ViewWeekIcon}
          />
        )}
        {features?.SupplyWalkEditForHubs && (
          <MenuItem
            title="Supply Walk"
            link="/supply-walk-single-for-hubs"
            Icon={ViewWeekIcon}
          />
        )}

        {features?.Inventory && (
          <MenuItem
            title="Inventory"
            link="/inventory"
            Icon={Inventory2OutlinedIcon}
          />
        )}
        {features?.TransferPlanning && (
          <MenuItem
            title="Transfer Planning"
            link="/transfer-planning"
            Icon={LocalShippingIcon}
          />
        )}
        {features?.AvantiCardRuns && (
          <MenuItem
            title="Avanti Card Runs"
            link="/avanti-card-runs"
            Icon={PrintIcon}
          />
        )}
        {features?.POExportDefault && (
          <MenuItem title="PO Export" link="/po-export" Icon={ChecklistIcon} />
        )}
        {features?.POExportWithAtomicMaster && (
          <MenuItem
            title="PO Export"
            link="/purchase-order-export-peach-and-lily"
            Icon={FileDownloadIcon}
          />
        )}
        {features?.POInbox && (
          <MenuItem title="PO Inbox (Beta)" link="/po-inbox" Icon={EditIcon} />
        )}
        {features?.PurchasingCalendar && (
          <MenuItem
            title="Purchasing Calendar"
            link="/purchasing-calendar"
            Icon={CalendarMonthIcon}
          />
        )}
        {features?.ContainerMapping && (
          <MenuItem
            title="Container Mapping"
            link="/container-mapping"
            Icon={WidgetsOutlinedIcon}
          />
        )}
        {features?.ManufacturerStock && (
          <MenuItem
            title="Inventory at Manufacturers"
            link="/manufacturer-stock"
            Icon={FactoryOutlinedIcon}
          />
        )}
      </Box>

      {/* Nucleus - Always at bottom */}
      <Box
        sx={{
          flexShrink: 0,
          width: "100%",
        }}
      >
        <Divider flexItem variant="middle" />
        {/* Nucleus stays pinned at the foot of the rail. Help and Feedback
            moved up into the top header (they apply across every tab). */}
        <MenuItem
          title="Nucleus (Beta)"
          link="/chat"
          Icon={AutoFixHighOutlinedIcon}
          onClick={handleNucleusClick}
          isActiveOverride={location.pathname === "/chat" || isSidebarOpen}
          isDisabledOverride={false}
        />
      </Box>
    </Box>
  );
};
